import React, { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import Swal from "sweetalert2";

import { useSocket } from "../../hooks/useSocket";
import { WebSocketEventsMap } from "../../services/socket/events-map";
import { buttonIcon } from "../../utils/buttonsUtil";

import { ProgressDetail } from "./ProgressDetail";
import Progreso from "./Progreso";

export const RecentsAssignaments = ({
    activeTab,
    setRenderParentListProcessFlow,
    renderParentListProcessFlow
}) => {
    const { connect, disconnect, on, connected } = useSocket();

    const [detailView, setDetailView] = useState(false);
    const [detail, setDetail] = useState(null);
    const [data, setData] = useState([]);

    const normalizeItem = (item) => ({
        ...item,
        assignedCount: item.assignedCount ?? item.assigned_count ?? 0,
        failureCount: item.failureCount ?? item.failure_count ?? 0,
        processedCount: item.processedCount ?? item.processed ?? 0,
        total_data: item.total_data ?? item.total ?? 0,
    });

    const getData = useCallback(async () => {
        const token = window.localStorage.getItem("token");
        try {
            const response = await fetch(`${process.env.REACT_APP_ASSIGN_URL}/api/v1/assignOperator/getAllAssignmentParents`, {
                method: 'GET',
                headers: new Headers({
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }),
            });
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const result = await response.json();

            const apiData = result.items
                .map(normalizeItem)
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            setData(prevData => {
                const merged = apiData.map(apiItem => {
                    const existing = prevData.find(p => String(p.id) === String(apiItem.id));
                    if (!existing) return apiItem;
                    return {
                        ...apiItem,
                        assignedCount: Math.max(apiItem.assignedCount, existing.assignedCount ?? 0),
                        failureCount: Math.max(apiItem.failureCount, existing.failureCount ?? 0),
                        processedCount: Math.max(apiItem.processedCount, existing.processedCount ?? 0),
                    };
                });
                const apiIds = new Set(merged.map(item => String(item.id)));
                const orphans = prevData.filter(item => !apiIds.has(String(item.id)));
                return [...merged, ...orphans];
            });
        } catch (err) {
            console.log(err);
        }
    }, []);

    const handleParentReady = useCallback((payload) => {
        setData(prevData => {
            if (prevData.some(item => String(item.id) === String(payload.parentId))) {
                return prevData;
            }
            const newItem = {
                id: payload.parentId,
                total_data: payload.total,
                assignedCount: payload.inserted || 0,
                failureCount: payload.failed || 0,
                processedCount: 0,
                n_intents: 0,
                operator_name: payload.operatorName || null,
                createdAt: new Date().toISOString(),
                performance_time: null,
            };
            return [newItem, ...prevData];
        });
    }, []);

    const handleProgress = useCallback((progressData) => {
        setData(prevData => {
            return prevData.map(item => {
                if (String(item.id) === String(progressData.parentId)) {
                    return {
                        ...item,
                        assignedCount: progressData.totalAssigned,
                        failureCount: progressData.failuredAssignments,
                        processedCount: progressData.processed,
                        total_data: progressData.total,
                    };
                }
                return item;
            });
        });
    }, []);

    const handleRefresh = () => {
        getData();
    };

    const handleFinished = useCallback(() => {
        handleRefresh();
    }, [getData]);

    const handleCannotReintent = useCallback(() => {
        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se permiten más intentos',
        });
    }, []);

    const handleDetail = (parent) => {
        setDetail(parent);
        setDetailView(!detailView);
    };

    useEffect(() => {
        if (activeTab !== 'tab3') return;

        connect();

        const unsubProgress = on(WebSocketEventsMap.Assignment.PROGRESS_UPDATE, handleProgress);
        const unsubFinished = on(WebSocketEventsMap.Assignment.FINISHED, handleFinished);
        const unsubCannotReintent = on(WebSocketEventsMap.Assignment.CANNOT_REINTENT, handleCannotReintent);
        const unsubParentReady = on(WebSocketEventsMap.Assignment.PARENT_READY, handleParentReady);

        return () => {
            unsubProgress();
            unsubFinished();
            unsubCannotReintent();
            unsubParentReady();
            disconnect();
        };
    }, [activeTab, connect, disconnect, on, handleProgress, handleFinished, handleCannotReintent, handleParentReady]);

    useEffect(() => {
        getData();
        return () => {
            setRenderParentListProcessFlow(false);
        };
    }, [renderParentListProcessFlow, getData]);

    const renderParents = () => {
        if (data.length > 0) {
            return data.map(parent => {
                return (
                    <Progreso
                        key={parent.id}
                        fallidas={parent.failureCount}
                        total={parent.total_data}
                        procesadas={parent.processedCount}
                        asignadas={parent.assignedCount}
                        parent={parent}
                        nIntents={parent.n_intents}
                        operatorName={parent.operator_name}
                        handleDetail={() => handleDetail(parent)}
                    />
                );
            });
        }
        return null;
    };

    if (!detailView) {
        return (
            <>
                <Row>
                    <Col sm={4}>
                        <p className="mb-2 small">Estado de conexión <b>{ connected ? 'Conectado' : 'Desconectado'}</b></p>
                        <h4>Progreso</h4>
                    </Col>
                    <Col sm={8} className="text-right">
                        <div>
                            <h6>Estados</h6>
                            <span className="badge badge-primary mr-2">En Proceso</span>
                            <span className="badge badge-success mr-2">Asignadas</span>
                            <span className="badge badge-danger mr-2">Fallidas</span>
                        </div>
                    </Col>
                </Row>
                <div className="d-flex align-items-center">
                    <small>Listado de Procesos</small>
                    <button className="btn btn-sm ml-2" onClick={handleRefresh} title="Refrescar">
                        <i className={`${buttonIcon.Sync}`}></i>
                    </button>
                </div>
                <hr className="dropdown-divider" />
                {renderParents()}
            </>
        );
    }

    return (<ProgressDetail parent={detail} activeTab={activeTab} handleDetail={handleDetail} />);
};
